import { FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Container, Typography } from '@mui/material';

import PrintView from '@components/PrintView/PrintView';
import { InquiryEntryType } from '@interfaces/inquiries';
import { handleGetSharedInquiryResponse } from '@store/features/inquiries/share/actions';
import {
  inquiriesShareUrlErrorSelector,
  inquiriesShareUrlIsLoadingSelector,
  sharedInquiryResponseSelector,
  sharedInquiryResponseTypeSelector,
} from '@store/features/inquiries/share/selectors';
import { removeSharedInquiryResponseDetails } from '@store/features/inquiries/share/slice';
import useDispatch from '@store/hooks/useDispatch';

import './style.scss';

const SharedInquiryResponsePage: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { sharedInquiryResponseId } = useParams();

  const data = useSelector(sharedInquiryResponseSelector);
  const inquiryType: null | InquiryEntryType = useSelector(sharedInquiryResponseTypeSelector);
  const isLoading = useSelector(inquiriesShareUrlIsLoadingSelector);
  const error = useSelector(inquiriesShareUrlErrorSelector);

  const handleFetchSharedInquiryResponse = useCallback(() => {
    sharedInquiryResponseId && dispatch(handleGetSharedInquiryResponse(sharedInquiryResponseId));
  }, [dispatch, sharedInquiryResponseId]);

  useEffect(handleFetchSharedInquiryResponse, [handleFetchSharedInquiryResponse]);

  useEffect(
    () => () => {
      dispatch(removeSharedInquiryResponseDetails());
    },
    [dispatch],
  );

  return (
    <Container maxWidth="lg" className="page shared-inquiry-response-page">
      <Typography variant="h1" className="shared-inquiry-response-page__title">
        {inquiryType === InquiryEntryType.Supplement && t('inquiryResponse.title')}
        {inquiryType === InquiryEntryType.Medication && t('inquiryResponse.medicationTitle')}
      </Typography>

      <PrintView
        data={data}
        error={error}
        inquiryType={inquiryType}
        getResponse={handleFetchSharedInquiryResponse}
        isLoading={isLoading}
      />
    </Container>
  );
};

export default SharedInquiryResponsePage;
